import React, { useEffect, useRef } from 'react';
import classnames from 'classnames';
import { IconButton } from '@highlight-ui/button';
import { noop } from '@highlight-ui/utils-commons';
import { InputContainer } from '@highlight-ui/input';

import styles from './InputTrigger.module.scss';
import { SelectOption } from '../types';
import { PopoverTriggerProps } from '../Popover';

export type InputTriggerProps<T extends SelectOption> = Partial<
  Pick<
    PopoverTriggerProps<T>,
    | 'disabled'
    | 'label'
    | 'triggerId'
    | 'outline'
    | 'autoFocus'
    | 'variant'
    | 'visible'
    | 'onClick'
    | 'selectedOptions'
    | 'onKeyDown'
  >
>;

const InputTrigger = React.forwardRef(
  <T extends SelectOption>(
    {
      disabled,
      label,
      outline = 'default',
      autoFocus = false,
      triggerId,
      variant,
      visible,
      onClick = noop,
      onKeyDown = noop,
      selectedOptions = [],
    }: InputTriggerProps<T>,
    ref: React.Ref<HTMLDivElement>,
  ) => {
    const textContainerRef = useRef<HTMLDivElement>(null);
    const hasSelectedOption = selectedOptions.length > 0;
    const [firstItem] = selectedOptions;

    useEffect(() => {
      if (autoFocus && textContainerRef.current) {
        textContainerRef.current.focus();
      }
    }, [autoFocus]);

    return (
      <InputContainer
        containerRef={ref}
        outline={outline}
        onOutlineClick={onClick}
        className={classnames(styles.selectTrigger, {
          [styles.fullWidth]: variant === 'full-width',
          [styles.disabled]: disabled,
        })}
        inputClassName={classnames(styles.textContainer, {
          [styles.selected]: hasSelectedOption,
        })}
        disabled={disabled}
        suffix={
          <IconButton
            icon={visible ? 'angle-up' : 'angle-down'}
            variant="plain"
            tabIndex={-1}
            aria-label="Toggle visibility"
            className={styles.icon}
            buttonState={disabled ? 'disabled' : 'default'}
          />
        }
      >
        <div
          id={triggerId}
          ref={textContainerRef}
          role="button"
          onKeyDown={onKeyDown}
          tabIndex={disabled ? -1 : 0}
        >
          <span className={styles.labelText}>{firstItem?.label || label}</span>
        </div>
      </InputContainer>
    );
  },
);

export default InputTrigger;
